import React from 'react';

export function RequiredLabel({ children }: { children: React.ReactNode }) {
  return (
    <>
      {children}
      <span className="text-red-500">*</span>
    </>
  );
}

function CustomRadio({ checked }: { checked: boolean }) {
  return (
    <span
      className={`w-4 h-4 rounded-full border-2 flex items-center justify-center transition-colors ${
        checked ? 'border-[#0AC1CE]' : 'border-gray-300'
      }`}
    >
      {checked && <span className="w-2 h-2 rounded-full bg-[#0AC1CE]" />}
    </span>
  );
}

function CustomCheckbox({ checked }: { checked: boolean }) {
  return (
    <span
      className={`w-4 h-4 rounded border-2 flex items-center justify-center transition-colors ${
        checked ? 'bg-[#0AC1CE] border-[#0AC1CE]' : 'bg-white border-gray-300'
      }`}
    >
      {checked && (
        <svg className="w-2.5 h-2.5 text-white" viewBox="0 0 10 8" fill="none">
          <path
            d="M1 4L3.5 6.5L9 1"
            stroke="currentColor"
            strokeWidth="1.5"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      )}
    </span>
  );
}

export function RadioQuestion({
  label,
  name,
  value,
  onChange,
}: {
  label: React.ReactNode;
  name: string;
  value: string;
  onChange: (v: string) => void;
}) {
  return (
    <div className="flex flex-col gap-2">
      <p className="font-medium text-black">{label}</p>
      <div className="flex gap-6">
        {['Тийм', 'Үгүй'].map((opt) => (
          <label key={opt} className="flex items-center gap-2 cursor-pointer">
            <span className="relative flex shrink-0 items-center justify-center">
              <input
                type="radio"
                name={name}
                value={opt}
                checked={value === opt}
                onChange={() => onChange(opt)}
                className="sr-only"
              />
              <CustomRadio checked={value === opt} />
            </span>
            {opt}
          </label>
        ))}
      </div>
    </div>
  );
}

export function CheckboxQuestion({
  label,
  options,
  value,
  onChange,
  columns = 1,
}: {
  label: React.ReactNode;
  options: { id: string; title: string }[];
  value: string[];
  onChange: (v: string[]) => void;
  columns?: 1 | 2;
}) {
  const toggle = (id: string) =>
    onChange(
      value.includes(id) ? value.filter((v) => v !== id) : [...value, id]
    );

  return (
    <div className="flex flex-col gap-2">
      <p className="font-medium text-black">{label}</p>
      <div
        className={
          columns === 2
            ? 'grid grid-cols-2 gap-x-4 gap-y-2'
            : 'flex flex-col gap-2'
        }
      >
        {options.map((opt) => (
          <label key={opt.id} className="flex items-center gap-2 cursor-pointer">
            <span className="relative flex shrink-0 items-center justify-center">
              <input
                type="checkbox"
                checked={value.includes(opt.id)}
                onChange={() => toggle(opt.id)}
                className="sr-only"
              />
              <CustomCheckbox checked={value.includes(opt.id)} />
            </span>
            {opt.title}
          </label>
        ))}
      </div>
    </div>
  );
}

export function SingleChoiceFromOptions({
  label,
  name,
  options,
  value,
  onChange,
}: {
  label: React.ReactNode;
  name: string;
  options: { id: string; title: string }[];
  value: string;
  onChange: (v: string) => void;
}) {
  return (
    <div className="flex flex-col gap-2">
      <p className="font-medium text-black">{label}</p>
      <div className="flex flex-col gap-2">
        {options.map((opt) => (
          <label key={opt.id} className="flex items-center gap-2 cursor-pointer">
            <span className="relative flex shrink-0 items-center justify-center">
              <input
                type="radio"
                name={name}
                value={opt.id}
                checked={value === opt.id}
                onChange={() => onChange(opt.id)}
                className="sr-only"
              />
              <CustomRadio checked={value === opt.id} />
            </span>
            {opt.title}
          </label>
        ))}
      </div>
    </div>
  );
}
